import axios, { AxiosResponse } from "axios";
import { DivIcon, Map as LeafletMap } from "leaflet";
import * as Constants from "../../constants";
import { getLocalCurrentDateString, parseTimestampToUTC } from "../../utils/date";

export interface Tornado {
  id: string;
  pinpoint: [number, number];
  probability: number;
  size: number;
}

export interface TornadoMarker extends Tornado {
  icon: any;
  timestamp: string;
  date: string;
  isHistorical: boolean;
  ageInMinutes: number;
  station: string;
}

export interface TornadoResponse {
  radar_stations: Record<string, Record<string, Tornado[]>>;
}

export class TornadoService {
  map: LeafletMap;
  pollingInterval: NodeJS.Timeout | null = null;
  dayChangeTimeout: NodeJS.Timeout | null = null;
  isPolling: boolean = false;
  tornados: TornadoResponse | null = null;
  iconCache: Map<string, DivIcon> = new Map();
  currentLocalDate: string;

  constructor(map: LeafletMap) {
    this.map = map;
    this.pollingInterval = null;
    this.dayChangeTimeout = null;
    this.isPolling = false;
    this.currentLocalDate = getLocalCurrentDateString();
  }

  async init(onDataUpdate: (markers: TornadoMarker[]) => void): Promise<void> {
    try {
      await this.fetchAndUpdateData(onDataUpdate);

      this.startPolling(onDataUpdate);
      this.scheduleDayChangeUpdate(onDataUpdate);
    } catch (e) {
      console.error("Initialization failed: ", e);
      throw e;
    }
  }

  async fetchAndUpdateData(onDataUpdate: (markers: TornadoMarker[]) => void) {
    try {
      await this.fetchTornadoData();
      this.updateMarkers(onDataUpdate);
    } catch (e) {
      console.error("Failed to fetch tornado data: ", e);
    }
  }

  updateMarkers(onDataUpdate: (markers: TornadoMarker[]) => void) {
    const markers = this.getMarkers();
    onDataUpdate(markers);
  }

  // Polling every 30 seconds
  startPolling(onDataUpdate: (markers: TornadoMarker[]) => void) {
    if (this.isPolling) return;
    this.isPolling = true;
    this.pollingInterval = setInterval(async () => {
      await this.fetchAndUpdateData(onDataUpdate);
    }, 30000);
  }

  scheduleDayChangeUpdate(onDataUpdate: (markers: TornadoMarker[]) => void) {
    if (this.dayChangeTimeout) {
      clearTimeout(this.dayChangeTimeout);
    }

    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0); 
    
    const msUntilMidnight = tomorrow.getTime() - now.getTime();

    this.dayChangeTimeout = setTimeout(() => {
      this.currentLocalDate = getLocalCurrentDateString();
      this.fetchAndUpdateData(onDataUpdate);
      this.scheduleDayChangeUpdate(onDataUpdate);
    }, msUntilMidnight);
  }

  async fetchTornadoData() {
    if (!Constants.DETECT_TORNADO_API_URL) {
      // tornado is disabled in this build — skip everything
      this.tornados = null;
      return;
    }

    try {
      const data = {
        date: getLocalCurrentDateString(),
      };

      const response: AxiosResponse<TornadoResponse> = await axios.post(
        Constants.DETECT_TORNADO_API_URL ?? "",
        data,
        {
          headers: {
            Authorization: `Bearer ${Constants.BEARER_TOKEN}`,
          },
        }
      );
      this.tornados = response.data;
    } catch (error) {
      console.log(error);
    }
  }

  getColorFromAge(ageInMinutes: number): string {
    if (ageInMinutes === undefined || ageInMinutes === null || isNaN(ageInMinutes)) {
      return "#000000";
    }

    const bracketSize = 180; // 3 hours

    const maxBrackets = 8;

    const bracketIndex = Math.min(
      Math.max(Math.floor(ageInMinutes / bracketSize), 0),
      maxBrackets - 1
    );

    const bracketColors = [
      "#000000",
      "#333333",
      "#555555",
      "#777777",
      "#888888",
      "#999999",
      "#bbbbbb",
      "#dddddd",
    ];

    return bracketColors[bracketIndex] || bracketColors[0];
  }

  getIcon(
    size: number,
    isHistorical: boolean = false,
    ageInMinutes: number = 0
  ): DivIcon {
    const color = this.getColorFromAge(ageInMinutes);

    const key = `${size}-${isHistorical ? "historical" : "current"}-${color}`;
    if (this.iconCache.has(key)) {
      return this.iconCache.get(key) as DivIcon;
    }

    const actualSize = isHistorical ? size * 0.5 : size;

    const icon = new DivIcon({
      html: `<div style="width: ${actualSize}px; height: ${actualSize}px; background-color: ${color}; border-radius: 100%;"></div>`,
      className: "tornado-icon",
    });
    this.iconCache.set(key, icon);
    return icon;
  }

  calculateAgeInMinutes(dateString: string, timeString: string): number {
    const detectionTime = parseTimestampToUTC(dateString, timeString);
    const currentUTCMs = Date.now();
    
    const diffMs = currentUTCMs - detectionTime.getTime();
    
    return diffMs / (1000 * 60);
  }

  getMarkers(): TornadoMarker[] {
    let updatedData: TornadoMarker[] = [];
    const currentZoom = this.map.getZoom();
    let size = currentZoom * 4;

    if (currentZoom <= 3) {
      size = currentZoom * 3.5;
    } else  {
      size =  Math.max(15, 21 - (currentZoom * 0.5));
    }

    if (this.tornados && this.tornados.radar_stations) {
      const { radar_stations } = this.tornados;      

      Object.keys(radar_stations).forEach((stationKey) => {
        const stationData = radar_stations[stationKey];
        const sortedTimestamps = Object.keys(stationData).sort().reverse();

        sortedTimestamps.forEach((timestamp, index) => {
          const isHistorical = index > 0;
          const detections = stationData[timestamp];

          if (detections && detections.length) {
            const currentDate = getLocalCurrentDateString();
            const ageInMinutes = this.calculateAgeInMinutes(currentDate, timestamp);

            detections.forEach((detection) => {

              // Only show the historical data when user zooms the map
              if (isHistorical && currentZoom < 6) {
                return;
              }

              const icon = this.getIcon(size, isHistorical, ageInMinutes);
              updatedData.push({
                ...detection,
                icon: icon,
                timestamp: timestamp,
                date: currentDate,
                isHistorical: isHistorical,
                ageInMinutes: ageInMinutes,
                station: stationKey
              });
            });
          }
        });
      });
    }
    return updatedData;
  }

  cleanup(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    if (this.dayChangeTimeout) {
      clearTimeout(this.dayChangeTimeout);
      this.dayChangeTimeout = null;
    }
    this.isPolling = false;
    this.iconCache.clear();
  }
}
